import { createClient } from '@sanity/client'
import imageUrlBuilder from '@sanity/image-url'
import { SanityImage, Movie } from '../types/movie' // Added Movie type

export const sanityClient = createClient({ // Renamed client to sanityClient and export
  projectId: import.meta.env.VITE_SANITY_PROJECT_ID,
  dataset: import.meta.env.VITE_SANITY_DATASET,
  useCdn: true,
  apiVersion: import.meta.env.VITE_SANITY_API_VERSION,
})

const builder = imageUrlBuilder(sanityClient) // Use renamed sanityClient

export function urlFor(source: SanityImage): string {
  if (!source) return ''
  return builder.image(source).url()
}

export async function getMovieBySlug(slug: string) {
  const query = `*[_type == "movie" && slug.current == $slug][0] {
    _id,
    title,
    releaseDate,
    hypeLevel,
    poster,
    posterCopyright,
    synopsis,
    synopsisCopyright,
    "slug": slug.current,
    "director": director->{
      _id,
      name,
      "image": photo,
      imdbId
    },
    "directors": directors[]-> {
      _id,
      name,
      "image": photo,
      imdbId
    },
    "mainActors": mainActors[]-> {
      _id,
      name,
      "image": photo,
      imdbId
    },
    "trailers": trailers[]-> | order(isMainTrailer desc) | order(releaseDate desc) {
      _id,
      title,
      "videoUrl": videoUrl,
      platform,
      isMainTrailer
    }
  }`

  try {
    const result = await sanityClient.fetch(query, { slug }) // Use renamed sanityClient
    return result
  } catch (error) {
    console.error('Error fetching movie by slug:', error)
    throw error
  }
}

export async function getCurrentMovies() {
  const today = new Date()
  const oneMonthAgo = new Date(today)
  oneMonthAgo.setMonth(today.getMonth() - 1)
  oneMonthAgo.setHours(0, 0, 0, 0)

  const query = `*[_type == "movie" && defined(releaseDate) && 
    releaseDate > $oneMonthAgo && 
    releaseDate <= $today
  ] | order(releaseDate desc, hypeLevel desc) {
    _id,
    title,
    releaseDate,
    hypeLevel,
    poster,
    synopsis,
    "slug": slug.current,
    "director": director->{
      _id,
      name
    },
    "directors": directors[]-> {
      _id,
      name
    }
  }`

  try {
    const result = await sanityClient.fetch(query, { // Use renamed sanityClient
      oneMonthAgo: oneMonthAgo.toISOString(),
      today: today.toISOString()
    })
    return result
  } catch (error) {
    console.error('Error fetching current movies:', error)
    throw error
  }
}

export async function getUpcomingMovies() {
  const today = new Date()
  const threeMonthsAhead = new Date(today)
  threeMonthsAhead.setMonth(today.getMonth() + 3)
  threeMonthsAhead.setHours(23, 59, 59, 999)

  const query = `*[_type == "movie" && defined(releaseDate) && 
    releaseDate > $today && 
    releaseDate <= $threeMonthsAhead
  ] | order(releaseDate asc, hypeLevel desc) {
    _id,
    title,
    releaseDate,
    hypeLevel,
    poster,
    synopsis,
    "slug": slug.current,
    "director": director->{
      _id,
      name
    },
    "directors": directors[]-> {
      _id,
      name
    }
  }`

  try {
    const result = await sanityClient.fetch(query, { // Use renamed sanityClient
      today: today.toISOString(),
      threeMonthsAhead: threeMonthsAhead.toISOString()
    })
    return result
  } catch (error) {
    console.error('Error fetching upcoming movies:', error)
    throw error
  }
}

export async function getFutureMovies(limit: number = 10, offset: number = 0) {
  const threeMonthsAhead = new Date()
  threeMonthsAhead.setMonth(threeMonthsAhead.getMonth() + 3)
  threeMonthsAhead.setHours(0, 0, 0, 0)

  const query = `*[_type == "movie" && defined(releaseDate) &&
    releaseDate > $threeMonthsAhead
  ] | order(releaseDate asc) [$offset...($offset + $limit)] {
    _id,
    title,
    releaseDate,
    "slug": slug.current
  }`

  try {
    const result = await sanityClient.fetch(query, { // Use renamed sanityClient
      threeMonthsAhead: threeMonthsAhead.toISOString(),
      limit,
      offset
    })
    return result
  } catch (error) {
    console.error('Error fetching future movies:', error)
    throw error
  }
}

export async function getAllMovies() {
  const query = `*[_type == "movie"] | order(hypeLevel desc) {
    _id,
    title,
    releaseDate,
    hypeLevel,
    poster,
    synopsis,
    "slug": slug.current,
    "director": director->{
      _id,
      name
    },
    "directors": directors[]-> {
      _id,
      name
    }
  }`

  try {
    const result = await sanityClient.fetch(query) // Use renamed sanityClient
    return result
  } catch (error) {
    console.error('Error fetching all movies:', error)
    throw error
  }
}

export async function getOldMovies(page: number = 0, limit: number = 20) {
  const today = new Date()
  const oneMonthAgo = new Date(today)
  oneMonthAgo.setMonth(today.getMonth() - 1)
  oneMonthAgo.setHours(0, 0, 0, 0)

  const query = `*[_type == "movie" && defined(releaseDate) &&
    releaseDate < $oneMonthAgo
  ] | order(releaseDate desc) [$start...$end] {
    _id,
    title,
    releaseDate,
    hypeLevel,
    poster,
    synopsis,
    "slug": slug.current,
    "director": director->{
      _id,
      name
    },
    "directors": directors[]-> {
      _id,
      name
    }
  }`

  const start = page * limit
  const end = start + limit - 1

  try {
    const result = await sanityClient.fetch(query, { // Use renamed sanityClient
      oneMonthAgo: oneMonthAgo.toISOString(),
      start,
      end
    })
    return result
  } catch (error) {
    console.error('Error fetching old movies:', error)
    throw error
  }
}

export async function getOldMoviesCount() {
  const today = new Date()
  const oneMonthAgo = new Date(today)
  oneMonthAgo.setMonth(today.getMonth() - 1)
  oneMonthAgo.setHours(0, 0, 0, 0)

  const query = `count(*[_type == "movie" && defined(releaseDate) &&
    releaseDate < $oneMonthAgo])`

  try {
    const result = await sanityClient.fetch(query, { // Use renamed sanityClient
      oneMonthAgo: oneMonthAgo.toISOString()
    })
    return result
  } catch (error) {
    console.error('Error fetching old movies count:', error)
    throw error
  }
}

export async function getMoviesByIds(ids: string[]): Promise<Movie[]> {
  if (!ids || ids.length === 0) {
    return [];
  }
  const query = `*[_type == "movie" && _id in $ids] {
    _id,
    title,
    releaseDate,
    hypeLevel,
    poster,
    posterCopyright,
    synopsis,
    synopsisCopyright,
    "slug": slug.current,
    "director": director->{
      _id,
      name
    },
    "directors": directors[]-> {
      _id,
      name
    }
  }`;

  try {
    const result = await sanityClient.fetch(query, { ids });
    return result;
  } catch (error) {
    console.error('Error fetching movies by IDs:', error);
    throw error;
  }
}

export interface TrailerWithMovie {
  _id: string;
  title: string;
  videoUrl: string;
  platform: 'youtube' | 'vimeo';
  isMainTrailer: boolean;
  releaseDate: string;
  movie: {
    _id: string;
    title: string;
    slug: string;
  } | null;
}

export async function getRecentTrailers(limit: number = 10): Promise<TrailerWithMovie[]> {
  const query = `*[_type == "trailer"] | order(releaseDate desc) [0...$limit] {
    _id,
    title,
    videoUrl,
    platform,
    isMainTrailer,
    releaseDate,
    "movie": *[_type == "movie" && references(^._id)][0] {
      _id,
      title,
      "slug": slug.current
    }
  }`;

  try {
    const result = await sanityClient.fetch(query, { limit });
    return result;
  } catch (error) {
    console.error('Error fetching recent trailers:', error);
    throw error;
  }
}

export async function getAllTrailers(
  limit: number = 20,
  offset: number = 0,
  mainOnly: boolean = false
): Promise<TrailerWithMovie[]> {
  const filter = mainOnly
    ? `*[_type == "trailer" && isMainTrailer == true]`
    : `*[_type == "trailer"]`;

  const query = `${filter} | order(releaseDate desc) [$offset...($offset + $limit)] {
    _id,
    title,
    videoUrl,
    platform,
    isMainTrailer,
    releaseDate,
    "movie": *[_type == "movie" && references(^._id)][0] {
      _id,
      title,
      "slug": slug.current
    }
  }`;

  try {
    const result = await sanityClient.fetch(query, { limit, offset });
    return result;
  } catch (error) {
    console.error('Error fetching all trailers:', error);
    throw error;
  }
}